import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, CircularProgress } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import "./DemoLoadingModal.css";

const REACT_APP_BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Must stay above the server's long-poll hold time (25s).
const PROGRESS_CLIENT_TIMEOUT_MS = 35000;
const PROGRESS_RETRY_DELAY_MS = 2000;

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// risk_level: 0 = no risk, 1 = attention needed, 2 = risk found
function checkKind(check) {
  if (check.status === "COMPLETED") {
    const risk = check.risk_level ?? 0;
    if (risk >= 2) return "risk";
    if (risk === 1) return "attention";
    return "ok";
  }
  if (check.status === "NOT_APPLICABLE") return "not-applicable";
  if (check.status === "IN_PROGRESS") return "in-progress";
  return "pending";
}

const DemoLoadingModal = ({ visible, setVisible, requestId }) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState("");
  const [checks, setChecks] = useState([]);
  const [pagesTotal, setPagesTotal] = useState(0);
  const [pagesProcessed, setPagesProcessed] = useState(0);

  useEffect(() => {
    if (!visible || !requestId) {
      setProgress(0);
      setStatus("");
      setChecks([]);
      setPagesTotal(0);
      setPagesProcessed(0);
      return;
    }

    let cancelled = false;
    let controller = null;

    // Long-poll get_progress: the server holds the request open until the
    // content changes relative to the fingerprint we send (or ~25s passes),
    // so we re-request immediately instead of polling on an interval.
    const poll = async () => {
      let fingerprint = null;

      while (!cancelled) {
        try {
          controller = new AbortController();
          const timeoutId = setTimeout(
            () => controller.abort(),
            PROGRESS_CLIENT_TIMEOUT_MS
          );

          const response = await fetch(
            `${REACT_APP_BACKEND_URL}/run/get_progress/${requestId}` +
              (fingerprint
                ? `?fingerprint=${encodeURIComponent(fingerprint)}`
                : ""),
            { signal: controller.signal }
          );

          clearTimeout(timeoutId);

          if (!response.ok) {
            await delay(PROGRESS_RETRY_DELAY_MS);
            continue;
          }

          const data = await response.json();
          fingerprint = data.fingerprint || fingerprint;

          if (cancelled) return;

          setProgress(data.progress || 0);
          setStatus(data.status || "");
          setChecks(Array.isArray(data.checks) ? data.checks : []);
          setPagesTotal(data.pages_total || 0);
          setPagesProcessed(data.pages_processed || 0);

          if ((data.progress || 0) >= 100 || data.status === "failed") {
            return;
          }
        } catch (error) {
          if (cancelled) return;
          await delay(PROGRESS_RETRY_DELAY_MS);
        }
      }
    };

    poll();

    return () => {
      cancelled = true;
      if (controller) {
        controller.abort();
      }
    };
  }, [visible, requestId]);

  // Resolved checks (completed or not applicable) stack on top, then the
  // in-progress one, then pending.
  const statusRank = { COMPLETED: 0, NOT_APPLICABLE: 0, IN_PROGRESS: 1 };
  const sortedChecks = [...checks].sort((a, b) => {
    const rankDiff = (statusRank[a.status] ?? 2) - (statusRank[b.status] ?? 2);
    return rankDiff !== 0
      ? rankDiff
      : Number(a.check_number) - Number(b.check_number);
  });
  const totalChecks = sortedChecks.length;
  const completedCount = sortedChecks.filter(
    (check) =>
      check.status === "COMPLETED" || check.status === "NOT_APPLICABLE"
  ).length;
  // Phase 1: reading/scanning the document (no checks yet).
  // Phase 2: executing checks (checks array is populated).
  const isChecksPhase = totalChecks > 0;
  const currentPage = Math.min(pagesProcessed + 1, pagesTotal);

  const statusFallback = status
    ? t(`maatwerkLoading.status.${status}`, status)
    : t("maatwerkLoading.subtitle");

  const subtitle = isChecksPhase
    ? ""
    : pagesTotal > 0
    ? t("maatwerkLoading.pageOf", { current: currentPage, total: pagesTotal })
    : statusFallback;

  const metaLeft = isChecksPhase
    ? t("maatwerkLoading.checksDone", {
        done: completedCount,
        total: totalChecks,
      })
    : pagesTotal > 0
    ? t("maatwerkLoading.pagesRead", {
        done: pagesProcessed,
        total: pagesTotal,
      })
    : "";

  const badgeLabels = {
    ok: t("maatwerkLoading.badge.noIssues"),
    attention: t("maatwerkLoading.badge.attention"),
    risk: t("maatwerkLoading.badge.risk"),
    "in-progress": t("maatwerkLoading.badge.inProgress"),
    "not-applicable": t("maatwerkLoading.badge.notApplicable"),
  };

  // Only the topmost in-progress check gets a spinner; pending checks get an
  // invisible spacer so the names stay aligned.
  const firstInProgressId = sortedChecks.find(
    (check) => check.status === "IN_PROGRESS"
  )?.check_id;

  const renderIcon = (kind, check) => {
    if (kind === "ok") {
      return (
        <span className="dlm-row-icon ok">
          <CheckIcon sx={{ fontSize: "17px" }} />
        </span>
      );
    }
    if (kind === "attention" || kind === "risk") {
      return <span className={`dlm-row-icon ${kind}`}>!</span>;
    }
    if (kind === "not-applicable") {
      return <span className="dlm-row-icon not-applicable">–</span>;
    }
    if (kind === "in-progress" && check.check_id === firstInProgressId) {
      return (
        <span className="dlm-row-icon spinner">
          <CircularProgress size={22} thickness={5} color="inherit" />
        </span>
      );
    }
    return <span className="dlm-row-icon spacer" />;
  };

  return (
    <Dialog
      open={visible}
      PaperProps={{
        sx: {
          backgroundColor: "#ffffff",
          borderRadius: "24px",
          overflow: "hidden",
          width: "620px",
          maxWidth: "94vw",
        },
      }}
    >
      <div className="dlm-header">
        <div className="dlm-header-top">
          <span className="dlm-header-spinner">
            <CircularProgress
              size={28}
              thickness={5}
              sx={{ color: "#439BD9" }}
            />
          </span>
          <div>
            <h2 className="dlm-header-title">
              {isChecksPhase
                ? t("maatwerkLoading.title")
                : t("maatwerkLoading.readingTitle")}
              ...
            </h2>
            {subtitle && <p className="dlm-header-subtitle">{subtitle}</p>}
          </div>
        </div>

        <div className="dlm-progress-track">
          <div
            className="dlm-progress-fill"
            style={{ width: `${Math.min(progress, 100)}%` }}
          />
        </div>
        <div className="dlm-progress-meta">
          <span>{metaLeft}</span>
          <span className="dlm-progress-pct">{progress}%</span>
        </div>
      </div>

      {isChecksPhase ? (
        <div className="dlm-list">
          {sortedChecks.map((check) => {
            const kind = checkKind(check);
            return (
              <div
                key={check.check_id || check.check_number}
                className={`dlm-row ${
                  kind === "in-progress" ? "active" : ""
                } ${kind === "pending" ? "pending" : ""} ${
                  kind === "not-applicable" ? "not-applicable" : ""
                }`}
              >
                {renderIcon(kind, check)}
                <span className="dlm-row-name">
                  {t(`check.${check.check_name}`, check.check_name)}
                </span>
                {kind !== "pending" && (
                  <span className={`dlm-badge ${kind}`}>
                    {badgeLabels[kind]}
                  </span>
                )}
              </div>
            );
          })}
        </div>
      ) : (
        <div className="dlm-reading">
          <div className="dlm-row active">
            <span className="dlm-row-icon spinner">
              <CircularProgress size={22} thickness={5} color="inherit" />
            </span>
            <span className="dlm-row-name wrap">
              {t("maatwerkLoading.readingRow")}
            </span>
            {pagesTotal > 0 && (
              <span className="dlm-badge in-progress">
                {t("maatwerkLoading.pagesBadge", {
                  done: pagesProcessed,
                  total: pagesTotal,
                })}
              </span>
            )}
          </div>

          <div className="dlm-info-card">
            <span className="dlm-info-icon">i</span>
            <div>
              <div className="dlm-info-title">
                {t("maatwerkLoading.goodToKnowTitle")}
              </div>
              <p className="dlm-info-text">
                {t("maatwerkLoading.goodToKnowText")}
              </p>
            </div>
          </div>
        </div>
      )}

    </Dialog>
  );
};

export default DemoLoadingModal;
